import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Sparkles, MessageSquare, RefreshCw, Users, Mail } from "lucide-react";
import { api } from "../lib/api";

export default function NewsletterReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [revising, setRevising] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.newsletterPreview(id).then(setData).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [id]);

  const requestChanges = async () => {
    if (!feedback.trim()) { toast.error("Escribe qué cambios quieres."); return; }
    setRevising(true);
    try {
      await api.reviseIssue(id, feedback.trim());
      toast.success("El agente aplicó tus cambios.");
      setFeedback("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "El agente no pudo revisar el borrador.");
    } finally { setRevising(false); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.sendIssue(id);
      toast.success(`Enviado a ${r.recipients} suscriptores.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar.");
    } finally { setSending(false); }
  };

  if (notFound)
    return (
      <div className="flex flex-col items-center py-24 text-center">
        <p className="font-display text-xl font-bold">Edición no encontrada</p>
        <Link to="/admin" className="mt-3 rounded-full bg-brand px-5 py-2 text-sm font-bold text-white">Volver al panel</Link>
      </div>
    );
  if (!data) return <div className="py-24 text-center text-muted-foreground">Cargando...</div>;

  const sent = data.status === "sent";

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <button onClick={() => nav("/admin")} data-testid="back-to-panel" className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
        <ArrowLeft className="h-4 w-4" /> Volver al panel
      </button>

      <div className="mt-3 grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Preview */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-brand-ink">{data.subject}</h1>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${sent ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{sent ? "Enviado" : "Listo para enviar"}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{data.preview_text}</p>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
            <iframe title="newsletter" srcDoc={data.html} data-testid="newsletter-iframe" className="h-[70vh] w-full bg-[#FDFBF7]" />
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-4">
            <h3 className="flex items-center gap-1.5 font-display font-bold text-brand-ink"><Mail className="h-4 w-4 text-brand" /> Detalles del envío</h3>
            <div className="mt-3 space-y-2 text-sm">
              <Row icon={Users} label="Destinatarios">{data.recipients} suscriptor{data.recipients === 1 ? "" : "es"}</Row>
              <Row icon={Sparkles} label="Bloques">{data.blocks?.length || 0}</Row>
              {data.sponsor && <Row icon={Sparkles} label="Patrocinador">{data.sponsor}</Row>}
              {sent && <Row icon={Mail} label="Métricas">{data.opens} aperturas · {data.clicks} clics</Row>}
            </div>
            {!sent && (
              <button data-testid="approve-send-btn" onClick={send} disabled={sending || data.recipients === 0} className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand font-bold text-white hover:bg-brand-hover disabled:opacity-60">
                <Send className="h-4 w-4" /> {data.recipients === 0 ? "Sin suscriptores aún" : sending ? "Enviando..." : "Aprobar y enviar"}
              </button>
            )}
          </div>

          <div className="flex flex-col rounded-2xl border border-border bg-card p-4" data-testid="agent-chat-panel">
            <h3 className="flex items-center gap-1.5 font-display font-bold text-brand-ink"><MessageSquare className="h-4 w-4 text-brand" /> Chatea con el agente</h3>
            <p className="mt-1 text-xs text-muted-foreground">Escríbele qué ajustar (tono, asunto, qué negocio destacar, acortar, etc.) y reescribirá el borrador.</p>

            {/* Conversation thread */}
            <div className="mt-3 max-h-80 space-y-3 overflow-y-auto" data-testid="agent-chat-thread">
              {(data.feedback_history?.length || 0) === 0 && (
                <div className="flex gap-2">
                  <AgentAvatar />
                  <div className="rounded-2xl rounded-tl-sm bg-secondary/60 px-3 py-2 text-xs text-brand-ink">
                    ¡Hola! Soy tu agente de newsletter. Dime qué quieres cambiar y lo reescribo al instante.
                  </div>
                </div>
              )}
              {data.feedback_history?.map((f, i) => (
                <div key={i} className="space-y-2" data-testid={`feedback-item-${i}`}>
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brand px-3 py-2 text-xs font-medium text-white" data-testid={`chat-user-${i}`}>{f.feedback}</div>
                  </div>
                  <div className="flex gap-2">
                    <AgentAvatar />
                    <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-secondary/60 px-3 py-2 text-xs text-brand-ink" data-testid={`chat-agent-${i}`}>
                      {f.reply || "Listo, apliqué tus cambios al borrador."}
                    </div>
                  </div>
                </div>
              ))}
              {revising && (
                <div className="flex gap-2">
                  <AgentAvatar />
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Reescribiendo el borrador...
                  </div>
                </div>
              )}
            </div>

            {sent ? (
              <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700">Este newsletter ya fue enviado, no se puede seguir editando.</p>
            ) : (
              <div className="mt-3 border-t border-border pt-3">
                <textarea
                  data-testid="feedback-input"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) requestChanges(); }}
                  rows={3}
                  placeholder="Ej: Haz el asunto más corto y destaca una panadería de Portland. Tono más cálido."
                  className="w-full resize-none rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/40"
                />
                <button data-testid="request-changes-btn" onClick={requestChanges} disabled={revising} className="mt-2 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-brand-ink text-sm font-bold text-white hover:opacity-90 disabled:opacity-60">
                  {revising ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {revising ? "El agente está reescribiendo..." : "Enviar al agente"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</span>
    <span className="font-semibold text-brand-ink">{children}</span>
  </div>
);

const AgentAvatar = () => (
  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
    <Sparkles className="h-3.5 w-3.5" />
  </div>
);
